import {
  Component,
  Input,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SelectChangeEvent, SelectModule } from '@openng/optimus-ui/select';
import { take } from 'rxjs';
import {
  CONTACT_DETAIL_CONTAINER,
  CONTACT_LIST_DETAIL_CONTAINER,
} from 'src/app/constants/common.constants';
import { clearFormArray } from 'src/app/core/functions/common-functions';
import { TabService } from 'src/app/core/services/tab.service';
import { ConfigContactListItem } from 'src/app/model/common/config-item';
import {
  AdditionalResourceList,
  CheckAdditionalResourceList,
} from 'src/app/model/contacts/additional-resource-list';
import { AdditionalResourceListType } from 'src/app/model/contacts/additional-resource-list-type';
import { ContactList } from 'src/app/model/contacts/contact-list';
import { ContactListMember } from 'src/app/model/contacts/contact-list-member';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { ContactListBuilderService } from 'src/app/services/builder/contact-list-builder.service';
import { ContactTableService } from 'src/app/services/table/contact-table.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ContactListCardComponent } from '../../../contacts/components/contact-list-card/contact-list-card.component';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { CardModule } from '@openng/optimus-ui/card';

@Component({
  selector: 'app-facility-contact-list-embed',
  imports: [
    ContactListCardComponent,
    SelectModule,
    TranslateModule,
    ReactiveFormsModule,
    ProgressSpinnerComponent,
    CardModule,
  ],
  templateUrl: './facility-contact-list-embed.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './facility-contact-list-embed.component.scss',
})
export class FacilityContactListEmbedComponent {
  protected _contactTable = inject(ContactTableService);
  protected _contactListApi = inject(ContactListApiService);
  protected _contactListBuilder = inject(ContactListBuilderService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _fb = inject(FormBuilder);
  protected _tabService = inject(TabService);

  @Input() contactList: ContactList | null = null;
  @Input() managementContactList: ContactList | null = null;
  readonly additionalResourceLists = input<AdditionalResourceList[]>([]);
  readonly additionalResourceListTypes = input<AdditionalResourceListType[]>(
    []
  );
  readonly contactListsForDropdowns = input<ConfigContactListItem[]>([]);
  readonly editMode = input<boolean>(false);
  readonly updateAdditionalResourceListEvent =
    output<CheckAdditionalResourceList>();
  readonly removeAdditionalResourceListEvent =
    output<AdditionalResourceListType>();

  form: FormGroup;
  additionalResourceListsArray: FormArray;
  contactListMembers: ContactListMember[] = [];
  resourceContactListOptions: ConfigContactListItem[] = [];
  loadingStates: boolean[] = [];

  constructor() {
    this.form = this._fb.group({
      additionalResourceLists: this._fb.array([]),
    });

    this.additionalResourceListsArray = this.form.get(
      'additionalResourceLists'
    ) as FormArray;
  }

  openContactList(id: string) {
    const tab = this._tabService.buildNewTab(
      CONTACT_LIST_DETAIL_CONTAINER,
      true,
      undefined,
      id
    );
    this._tabService.openTab(tab);
  }

  openProfile(id: string) {
    // clear out the selected to make sure a previously viewed contact isn't used.
    this._contactTable.setSelected(null);
    this._contactTable.setScreenEmbedded(false);
    const tab = this._tabService.buildNewTab(
      CONTACT_DETAIL_CONTAINER,
      true,
      undefined,
      id
    );
    this._tabService.openTab(tab);
  }

  getContactListOptions(
    resource: AdditionalResourceListType
  ): ConfigContactListItem[] {
    return this.contactListsForDropdowns().filter(
      (contactList) => contactList.additionalResourceListTypeId === resource.id
    );
  }

  updateForm(): void {
    this.form.reset();
    this.loadingStates = [];

    clearFormArray(this.additionalResourceListsArray);

    this.additionalResourceLists()?.forEach((resource) => {
      this.additionalResourceListsArray.push(
        this._fb.group({
          contactList: resource.id,
          AdditionalResourceListType: resource.AdditionalResourceListType,
        })
      );
      this.loadingStates.push(false);
    });

    this.additionalResourceListTypes().forEach((type) => {
      if (
        this.additionalResourceLists().some(
          (arl) => arl.AdditionalResourceListType.id === type.id
        )
      ) {
        return;
      }

      this.additionalResourceListsArray.push(
        this._fb.group({
          contactList: null,
          AdditionalResourceListType: type,
        })
      );
      this.loadingStates.push(false);
    });

    this.form.setControl(
      'additionalResourceLists',
      this.additionalResourceListsArray
    );
  }

  updateContactList(
    $event: SelectChangeEvent,
    resourceType: AdditionalResourceListType,
    i: number
  ) {
    const contactListId = $event.value;
    if (contactListId) {
      this.loadingStates[i] = true;
      this.getContactListMembers(contactListId, resourceType, i);
    } else {
      this.removeAdditionalResourceListEvent.emit(resourceType);
    }
  }

  getContactListMembers(
    contactListId: string,
    resourceType: AdditionalResourceListType,
    i: number
  ): void {
    this._contactListApi.getContactListMembers(contactListId).subscribe({
      next: (contactListMembers) => {
        this.contactListMembers = contactListMembers;
        this.assignAdditionalResource(contactListId, resourceType, i);
      },
      error: (error) => {
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'CONTACT.MESSAGES.ERROR.RETRIEVE_CONTACT_LIST_MEMBERS',
        });
      },
    });
  }

  assignAdditionalResource(
    contactListId: string,
    resourceType: AdditionalResourceListType,
    i: number
  ): void {
    // Check if updated list exists in existing additionalResourceLists
    const existingResource = this.additionalResourceLists().find(
      (resource) => resource.AdditionalResourceListType.id === resourceType.id
    );

    let retVal = {
      id: contactListId,
      name: this.contactListsForDropdowns().find(
        (list) => list.id === contactListId
      )?.name!,
      ContactListMembers: this.contactListMembers,
      AdditionalResourceListType: existingResource
        ? existingResource.AdditionalResourceListType
        : this.additionalResourceListTypes().find(
            (resource) => resource.id === resourceType.id
          )!,
      isExistingResource: existingResource ? true : false,
    };

    this.loadingStates[i] = false;
    this.updateAdditionalResourceListEvent.emit(retVal);
  }

  shouldRenderForm(): boolean {
    return (
      this.additionalResourceListsArray?.length >=
      this.additionalResourceListTypes().length
    );
  }
}
